/**
 * Generic Validation Context
 *
 * 100% domain-agnostic validation system.
 * Supports both flat and nested configuration structures.
 */

import React, {
  createContext,
  useContext,
  useRef,
  type ReactNode,
} from "react";
import { logger } from "@platform/browser-logger";

import { useAgGridUtils, AgGridContext } from "../context";
import { type CellReference } from "../utilities/agGridUtils";

import {
  validators,
  validateValue,
  type ValidatorName,
  type ValidatorMap,
  type CrossCellContext,
} from "./validators";

import type { GridApi } from "ag-grid-community";

/**
 * Configuration can be flat or nested (one level)
 *
 * Flat: { fieldName: ["validator1", "validator2"] }
 * Nested: { category: { fieldName: ["validator1", "validator2"] } }
 */
export type ValidationConfig = Record<
  string,
  ValidatorName[] | Record<string, ValidatorName[]>
>;

interface ValidationContextValue {
  /** Map of all available validators */
  validators: ValidatorMap;

  /** Get validators for a field (supports flat and nested config) */
  getValidators: (fieldOrCategory: string, field?: string) => ValidatorName[];

  /** Validate a value against multiple validators */
  validateValue: typeof validateValue;

  /** Get value from another cell */
  getCellValue: (field: string) => unknown;

  /** Refresh specific cells in the grid */
  refreshCells: (cells: CellReference[]) => void;

  /** AG Grid API instance */
  agGridApi?: GridApi;
}

const ValidationContext = createContext<ValidationContextValue | null>(null);

interface ValidationProviderProps {
  /** Map of validators to provide */
  validators?: ValidatorMap;

  /** Validation configuration (flat or nested) */
  validationConfig?: ValidationConfig;

  /** Current row data for cross-cell validation */
  currentRowData?: Record<string, unknown>;

  children: ReactNode;
}

/**
 * Hook to optionally get AgGrid utilities (returns null if not available)
 */
const useOptionalAgGridUtils = (): ReturnType<typeof useAgGridUtils> | null => {
  const context = useContext(AgGridContext);
  return context;
};

/**
 * Generic validation provider
 *
 * Usage:
 * <ValidationProvider
 *   validators={allValidators}
 *   validationConfig={config}
 * >
 *   {children}
 * </ValidationProvider>
 */
export const ValidationProvider: React.FC<ValidationProviderProps> = ({
  validators: providedValidators = validators,
  validationConfig = {},
  currentRowData = {},
  children,
}) => {
  const currentRowDataRef = useRef(currentRowData);
  currentRowDataRef.current = currentRowData;

  // Get AgGrid utilities from context (optional for testing)
  const agGridUtils = useOptionalAgGridUtils();

  /**
   * Get validators for a field
   * Supports: getValidators("fieldName") and getValidators("category", "fieldName")
   */
  const getValidators = (
    fieldOrCategory: string,
    field?: string,
  ): ValidatorName[] => {
    if (field) {
      // Nested: getValidators("category", "field")
      const categoryConfig = validationConfig[fieldOrCategory];
      if (
        categoryConfig &&
        typeof categoryConfig === "object" &&
        !Array.isArray(categoryConfig)
      ) {
        return categoryConfig[field] || [];
      }
      return [];
    }
    // Flat: getValidators("field")
    const fieldConfig = validationConfig[fieldOrCategory];
    if (Array.isArray(fieldConfig)) {
      return fieldConfig;
    }
    return [];
  };

  const contextValue: ValidationContextValue = {
    validators: providedValidators,
    getValidators,
    validateValue,

    getCellValue: (field: string) =>
      agGridUtils
        ? agGridUtils.getCellValue(field, currentRowDataRef.current)
        : currentRowDataRef.current[field],

    refreshCells: (cells: CellReference[]) => {
      if (agGridUtils) {
        agGridUtils.refreshCells(cells);
      }
    },

    agGridApi: agGridUtils?.api ?? undefined,
  };

  return (
    <ValidationContext.Provider value={contextValue}>
      {children}
    </ValidationContext.Provider>
  );
};

/**
 * Hook to access validation context
 *
 * Usage:
 * const { getValidators, validateValue, getCellValue } = useValidation();
 * const validators = getValidators("fieldName"); // flat config
 * const validators = getValidators("category", "fieldName"); // nested config
 */
export const useValidation = (): ValidationContextValue => {
  const context = useContext(ValidationContext);
  if (!context) {
    throw new Error("useValidation must be used within a ValidationProvider");
  }
  return context;
};

/**
 * Hook to validate a field
 *
 * Usage:
 * const { validateField } = useValidateField();
 * const result = validateField(value, "fieldName"); // flat config
 * const result = validateField(value, "category", "fieldName"); // nested config
 */
export const useValidateField = (): {
  validateField: (
    value: unknown,
    fieldOrCategory: string,
    field?: string,
    rowData?: Record<string, unknown>,
  ) => ReturnType<typeof validateValue>;
} => {
  const { getValidators, validateValue, getCellValue } = useValidation();

  const validateField = (
    value: unknown,
    fieldOrCategory: string,
    field?: string,
    rowData?: Record<string, unknown>,
  ): ReturnType<typeof validateValue> => {
    const validatorNames = getValidators(fieldOrCategory, field);

    const context: CrossCellContext = {
      rowData,
      getCellValue,
    };

    return validateValue(value, validatorNames, context);
  };

  return { validateField };
};

/**
 * Hook for AG Grid field validation
 *
 * Usage in AG Grid column definition:
 * const { createFieldValidator } = useAGGridValidation();
 *
 * cellClassRules: {
 *   "invalid-cell": createFieldValidator("fieldName") // flat config
 *   "invalid-cell": createFieldValidator("category", "fieldName") // nested config
 * }
 */
export const useAGGridValidation = (): {
  createFieldValidator: (
    fieldOrCategory: string,
    field?: string,
  ) => (params: { value: unknown; data?: Record<string, unknown> }) => boolean;
  createFieldChangeHandler: (
    fieldOrCategory: string,
    field?: string,
  ) => (params: { newValue: unknown; data?: Record<string, unknown> }) => void;
} => {
  const { getValidators, validateValue } = useValidation();

  const createFieldValidator =
    (fieldOrCategory: string, field?: string) =>
    (params: { value: unknown; data?: Record<string, unknown> }): boolean => {
      const validatorNames = getValidators(fieldOrCategory, field);
      if (validatorNames.length === 0) return false; // No validators = valid

      const context: CrossCellContext = {
        rowData: params.data,
        getCellValue: (fieldName: string) => params.data?.[fieldName],
      };

      const result = validateValue(params.value, validatorNames, context);
      return !result.isValid; // Return true for invalid (to apply CSS class)
    };

  const createFieldChangeHandler =
    (fieldOrCategory: string, field?: string) =>
    (params: { newValue: unknown; data?: Record<string, unknown> }): void => {
      const validatorNames = getValidators(fieldOrCategory, field);
      if (validatorNames.length === 0) return; // No validators

      const context: CrossCellContext = {
        rowData: params.data,
        getCellValue: (fieldName: string) => params.data?.[fieldName],
      };

      const result = validateValue(params.newValue, validatorNames, context);

      if (!result.isValid) {
        logger.warn(`Validation failed for field:`, {
          data: {
            field: field || fieldOrCategory,
            failedValidators: result.failedValidators,
          },
        });
        // Component can handle this as needed
      }
    };

  return {
    createFieldValidator,
    createFieldChangeHandler,
  };
};
